/**
 * The GIF picker sheet (R3) and its unavailable frame (R4), opened from the
 * composer's GIF action.
 *
 * R3: a `Search GIFs` field, a two-column grid that pages on scroll, a
 * `Trending` shelf before the fan types anything, and the provider's
 * attribution mark on every single tile. Tapping a tile calls back
 * immediately; there is no confirm step and no preview stage.
 *
 * R4: when `/gif/search` answers `isProviderAvailable: false`, the sheet
 * draws one centered neutral frame instead. No search field, no empty grid
 * standing in for results, and no `Try again` action, because retrying a
 * rate-limited provider straight away is very unlikely to succeed. The
 * brief's own anti-patterns list names both of those as things not to ship.
 */
import { useGifSearch } from "@sportsmet/messaging-sdk/private";
import { Dialog, Input, NEUTRAL_SURFACE_FRAME, Skeleton } from "@sportsmet/ui";
import type { ReactNode, UIEvent } from "react";
import { useEffect, useState } from "react";

import {
  GIF_ATTRIBUTION_MARK,
  GIF_NO_RESULTS_MESSAGE,
  GIF_PICKER_TITLE,
  GIF_SEARCH_PLACEHOLDER,
  GIF_TRENDING_SHELF_LABEL,
  GIF_UNAVAILABLE_BODY,
  GIF_UNAVAILABLE_TITLE,
} from "./chatExtrasCopy";
import {
  appendGifPage,
  GIF_SEARCH_STALE_TIME_MS,
  isShowingGifNoResults,
  isShowingTrendingShelf,
  readGifSearchResponse,
  resolveGifSearchQuery,
  type typeGifResult,
} from "./gifPickerLogic";

/** How close to the bottom of the grid asks for the next cursor page. */
const LOAD_MORE_THRESHOLD_PX = 240;

export type typeGifPickerSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** R3: fires on tap, with no confirm step in between. */
  onSelect: (providerGifId: string) => void;
};

/** The neutral mark for R4's frame. A plain outlined square, deliberately
 *  not a warning triangle or a struck-through glyph: a fan who tapped the
 *  GIF action did nothing wrong. */
function GifUnavailableIcon(): ReactNode {
  return (
    <svg
      className="h-icon-lg w-icon-lg"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="3"
        y="5"
        width="18"
        height="14"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M8.5 10.5h-1.5v3h1.5v-1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.5 10v4M14.5 14v-4h2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export const GifPickerSheet = (props: typeGifPickerSheetProps): ReactNode => {
  const [fieldValue, setFieldValue] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [items, setItems] = useState<typeGifResult[]>([]);

  const query = resolveGifSearchQuery(fieldValue);
  // Same `staleTime` as the composer's availability probe, so the probe's
  // cached trending page IS this sheet's first page and opening the picker
  // costs no second call.
  const search = useGifSearch(
    { query, cursor },
    { enabled: props.open, staleTime: GIF_SEARCH_STALE_TIME_MS },
  );
  const reading = readGifSearchResponse(search.data, search.isError);

  // A new query starts a new grid. Keyed on the resolved query rather than
  // the raw field so that "touchdown" and " touchdown " do not throw the
  // accumulated pages away and refetch the identical search.
  useEffect(() => {
    setCursor(null);
    setItems([]);
  }, [query]);

  // Fold each arriving page into the grid. `appendGifPage` returns the same
  // reference when a page adds nothing, so a re-render with cached data
  // does not restart this effect's own state.
  useEffect(() => {
    if (search.isPending || !reading.isProviderAvailable) return;
    setItems((prev) => appendGifPage(prev, reading.items));
    // `reading` is rebuilt every render; the page identity that actually
    // matters is the response the hook is holding, so that is what this
    // watches.
  }, [search.data, search.isPending, reading.isProviderAvailable]);

  const handleOpenChange = (next: boolean): void => {
    if (!next) {
      setFieldValue("");
      setCursor(null);
      setItems([]);
    }
    props.onOpenChange(next);
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
    if (reading.nextCursor === null || search.isFetching) return;
    const el = event.currentTarget;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining <= LOAD_MORE_THRESHOLD_PX) setCursor(reading.nextCursor);
  };

  const isUnavailable = !search.isPending && !reading.isProviderAvailable;
  const isFirstPageLoading = search.isPending && items.length === 0;
  const isNoResults = isShowingGifNoResults({
    fieldValue,
    isPending: search.isPending,
    isProviderAvailable: reading.isProviderAvailable,
    pageItemCount: reading.items.length,
    gridItemCount: items.length,
  });

  let body: ReactNode;
  if (isUnavailable) {
    body = (
      <div
        data-testid="gif-picker-unavailable"
        className={`flex flex-col items-center justify-center gap-2 rounded-md px-6 py-10 text-center text-ink-secondary ${NEUTRAL_SURFACE_FRAME}`}
      >
        <GifUnavailableIcon />
        <p className="text-body text-ink">{GIF_UNAVAILABLE_TITLE}</p>
        <p className="text-caption text-ink-tertiary">{GIF_UNAVAILABLE_BODY}</p>
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-2">
        <Input
          value={fieldValue}
          // The brief names one string for this field. It is the visible
          // placeholder AND the accessible name, rather than a second
          // invented label sitting above it.
          label={GIF_SEARCH_PLACEHOLDER}
          labelHidden={true}
          placeholder={GIF_SEARCH_PLACEHOLDER}
          data-testid="gif-search-input"
          onChange={(e) => {
            setFieldValue(e.target.value);
          }}
        />
        {isShowingTrendingShelf(fieldValue) ? (
          <p
            className="text-caption text-ink-tertiary"
            data-testid="gif-trending-label"
          >
            {GIF_TRENDING_SHELF_LABEL}
          </p>
        ) : null}
        {isNoResults ? (
          <p
            data-testid="gif-no-results"
            className="py-10 text-center text-body text-ink-secondary"
          >
            {GIF_NO_RESULTS_MESSAGE}
          </p>
        ) : (
          <div
            onScroll={handleScroll}
            data-testid="gif-result-grid"
            className="grid max-h-96 grid-cols-2 gap-2 overflow-y-auto"
          >
            {isFirstPageLoading
              ? [0, 1, 2, 3].map((slot) => (
                  <Skeleton
                    key={slot}
                    shape="card"
                    className="aspect-square w-full"
                  />
                ))
              : items.map((item) => (
                  <button
                    key={item.providerGifId}
                    type="button"
                    data-testid="gif-result-tile"
                    data-provider-gif-id={item.providerGifId}
                    onClick={() => {
                      props.onSelect(item.providerGifId);
                      handleOpenChange(false);
                    }}
                    className="relative aspect-square overflow-hidden rounded-md bg-surface-sunken"
                  >
                    <img
                      src={item.previewUrl}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                    {/* Mandatory, on every tile. Never removed for a cleaner
                      grid: it is a contractual requirement of the
                      provider's terms, not a style choice. */}
                    <span
                      data-testid="gif-result-attribution"
                      className="absolute right-0 bottom-0 bg-scrim px-1 text-caption text-on-primary"
                    >
                      {GIF_ATTRIBUTION_MARK}
                    </span>
                  </button>
                ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={handleOpenChange}
      title={GIF_PICKER_TITLE}
    >
      {body}
    </Dialog>
  );
};
